import { AlbumsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { from, of } from 'rxjs';
import { catchError, concatMap, last, map, switchMap, tap } from 'rxjs/operators';
import * as AlbumsActions from './albums.actions';

@Injectable()
export class AlbumsEffects {
  private actions$ = inject(Actions);
  private albumsApi = inject(AlbumsApiService);
  private notification = inject(NotificationService);

  loadAlbums$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.loadAlbums),
      switchMap(({ params }) =>
        this.albumsApi.findAll(params).pipe(
          map((response) => AlbumsActions.loadAlbumsSuccess({ response })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_albums_error');
            return of(AlbumsActions.loadAlbumsFailure({ error: error.message || 'Failed to load albums' }));
          }),
        ),
      ),
    ),
  );

  loadAlbum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.loadAlbum),
      switchMap(({ id }) =>
        this.albumsApi.findOne(id).pipe(
          map((album) => AlbumsActions.loadAlbumSuccess({ album })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_album_error');
            return of(AlbumsActions.loadAlbumFailure({ error: error.message || 'Failed to load album' }));
          }),
        ),
      ),
    ),
  );

  createAlbum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.createAlbum),
      switchMap(({ albumData }) =>
        this.albumsApi.create(albumData).pipe(
          map((album) => {
            this.notification.success('admin.notifications.album_created');
            return AlbumsActions.createAlbumSuccess({ album });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_album_error');
            return of(AlbumsActions.createAlbumFailure({ error: error.message || 'Failed to create album' }));
          }),
        ),
      ),
    ),
  );

  updateAlbum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.updateAlbum),
      switchMap(({ id, albumData }) =>
        this.albumsApi.update(id, albumData).pipe(
          map((album) => {
            this.notification.success('admin.notifications.album_updated');
            return AlbumsActions.updateAlbumSuccess({ album });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_album_error');
            return of(AlbumsActions.updateAlbumFailure({ error: error.message || 'Failed to update album' }));
          }),
        ),
      ),
    ),
  );

  deleteAlbum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.deleteAlbum),
      switchMap(({ id }) =>
        this.albumsApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.album_deleted');
            return AlbumsActions.deleteAlbumSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_album_error');
            return of(AlbumsActions.deleteAlbumFailure({ error: error.message || 'Failed to delete album' }));
          }),
        ),
      ),
    ),
  );

  uploadPhotos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.uploadPhotos),
      switchMap(({ id, files }) => {
        if (files.length === 0) {
          return of(AlbumsActions.uploadPhotosFailure({ error: 'No files to upload' }));
        }

        let lastAlbum: any = null;

        return from(files).pipe(
          concatMap((file) =>
            this.albumsApi.uploadPhoto(id, file).pipe(
              tap((album) => {
                lastAlbum = album;
              }),
              map((album) => AlbumsActions.uploadPhotoSuccess({ album })),
              catchError((error: any) => {
                this.notification.error(`Failed to upload ${ file.name }`);
                return of(AlbumsActions.uploadPhotosFailure({
                  error: error.message || `Failed to upload ${ file.name }`,
                }));
              }),
            ),
          ),
          last(),
          switchMap((lastAction) => {
            if (lastAction.type === AlbumsActions.uploadPhotoSuccess.type && lastAlbum) {
              this.notification.success('admin.notifications.photos_uploaded');
              return of(AlbumsActions.uploadPhotosComplete({ album: lastAlbum }));
            }
            return of(lastAction);
          }),
        );
      }),
    ),
  );

  deletePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.deletePhoto),
      switchMap(({ id, photoId }) =>
        this.albumsApi.deletePhoto(id, photoId).pipe(
          map((album) => {
            this.notification.success('admin.notifications.photo_deleted');
            return AlbumsActions.deletePhotoSuccess({ album });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_photo_error');
            return of(AlbumsActions.deletePhotoFailure({ error: error.message || 'Failed to delete photo' }));
          }),
        ),
      ),
    ),
  );

  setTitlePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlbumsActions.setTitlePhoto),
      switchMap(({ id, photoId }) =>
        this.albumsApi.setTitlePhoto(id, photoId).pipe(
          map((album) => {
            this.notification.success('admin.notifications.title_photo_set');
            return AlbumsActions.setTitlePhotoSuccess({ album });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.set_title_photo_error');
            return of(AlbumsActions.setTitlePhotoFailure({ error: error.message || 'Failed to set title photo' }));
          }),
        ),
      ),
    ),
  );
}

